import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth/store';
import { ApiError } from '../auth/api';
import { BrandHero } from '../brand/BrandHero';

export function SignupPage() {
  const navigate = useNavigate();
  const signup = useAuthStore((s) => s.signup);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Client-side policy mirrors the server's signup validation in
  // `signaling/src/routes/auth.ts`: 12 chars + 3-of-4 character classes. Failing fast in the
  // UI saves a round-trip and gives the user a precise error, rather than the generic
  // `invalid_body` the server returns.
  const localPasswordIssue = (pw: string): string | null => {
    if (pw.length < 12) return 'Password must be at least 12 characters';
    const classes = [
      /[a-z]/.test(pw),
      /[A-Z]/.test(pw),
      /[0-9]/.test(pw),
      /[^A-Za-z0-9]/.test(pw),
    ].filter(Boolean).length;
    if (classes < 3) {
      return 'Password must contain at least 3 of: lowercase, uppercase, digit, symbol';
    }
    return null;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const issue = localPasswordIssue(password);
    if (issue) {
      setError(issue);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await signup(email.trim().toLowerCase(), password, displayName.trim() || undefined);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'email_already_used') {
        setError('Email already registered');
      } else if (err instanceof ApiError && err.code === 'rate_limited') {
        setError(
          `Too many signups from this network; try again in ${err.retryAfterSeconds ?? 3600} seconds.`,
        );
      } else if (err instanceof ApiError && err.status === 400) {
        setError('Invalid email or password format');
      } else {
        setError('Sign-up failed; check your connection');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="brand-stage">
      <BrandHero subtitle="Create your account" />

      <aside className="brand-panel">
        <div className="brand-panel-inner">
          <h2 className="brand-panel-title">Sign up</h2>

          <form onSubmit={onSubmit} className="space-y-4" autoComplete="on">
            <Field label="Display name (optional)">
              <input
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="input-luxe text-sm"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-luxe text-sm"
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-luxe text-sm"
              />
              <span className="block mt-1 text-[10px] text-[var(--color-text-muted)]">
                12+ chars · at least 3 of: lowercase / uppercase / digit / symbol
              </span>
            </Field>
            <Field label="Confirm password">
              <input
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input-luxe text-sm"
              />
            </Field>
            {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="btn-gold w-full py-3 disabled:opacity-50 tracking-[0.2em] uppercase text-sm"
            >
              {submitting ? 'Creating…' : 'Create account'}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-[var(--color-text-muted)]">
            Already have an account?{' '}
            <Link to="/login" className="text-[var(--color-accent)] hover:underline">
              Sign in
            </Link>
          </p>
          <p className="mt-2 text-center text-xs text-[var(--color-text-muted)]">
            <Link to="/" className="hover:text-[var(--color-accent)] transition-colors">
              ← back to quick connect
            </Link>
          </p>
        </div>
      </aside>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.28em] text-[var(--color-text-muted)] mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
